"use client"

import { ArrowRight, Sparkles } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  closingDate,
  describeChange,
  latestChangeLine,
  learningTally,
} from "@/lib/conversation-quality/change-display"
import { questionWithKind } from "@/lib/conversation-quality/outcome-display"
import {
  instructionsRoomLabel,
  learningHeading,
  noChangesLine,
  roomLines,
} from "@/lib/conversation-quality/self-learning-display"
import type {
  QualitySuggestionsResult,
  SelfLearningSummaryResult,
  UnresolvedTopicsResult,
} from "@/server-actions/conversation-quality"

import { LearnersSetting } from "../self-learning/learners-setting"
import { ChangeStateBadge } from "./change-sections"

type Learners = SelfLearningSummaryResult["learners"]
type Suggestion = QualitySuggestionsResult["suggestions"][number]

const ROW = "flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:px-6"

/**
 * What self-learning did for the agent, or every agent: what waits for a decision as the headline, four numbers, the
 * question most worth learning, the latest change, and the mode.
 */
export function SelfLearningCard({
  data,
  suggestions,
  topics,
  timezone,
  onReview,
  onAllChanges,
  onQuestions,
}: {
  data: SelfLearningSummaryResult | undefined;
  suggestions: QualitySuggestionsResult | undefined;
  /** The period's; `undefined` while it loads or without finished sessions. */
  topics: UnresolvedTopicsResult | undefined;
  timezone: string;
  onReview: () => void;
  onAllChanges: () => void;
  onQuestions: () => void;
}) {
  if (!data) return <Skeleton className="h-56 w-full rounded-lg" />
  const { learners, latest } = data
  if (learners.scope === "agents" && learners.total === 0) return null

  const waiting = suggestions?.waiting ?? 0
  const topQuestion = mostAsked(topics)

  return (
    <Card className="min-w-0 overflow-hidden border-violet-200 dark:border-violet-900">
      <Headline
        learners={learners}
        waiting={waiting}
        listed={suggestions?.suggestions ?? []}
        tally={data.improvements === 0 ? noChangesLine(learners.scope, isLearning(learners)) : learningTally(learners.scope, data)}
        onReview={onReview}
      />
      <dl className="grid grid-cols-1 border-t border-violet-100 dark:border-violet-900/60 sm:grid-cols-2 lg:grid-cols-4">
        <Figure
          label="Learned so far"
          value={data.improvements.toLocaleString()}
          note={`${data.takenBack.toLocaleString()} taken back`}
        />
        <Figure
          label="Waiting for you"
          value={waiting.toLocaleString()}
          note={waitingNote(suggestions, timezone)}
          accent={waiting > 0}
        />
        <Figure
          label="Still to learn"
          value={(topics ? questionCount(topics) : 0).toLocaleString()}
          note="questions not resolved in this period"
        />
        <LearnersFigure learners={learners} />
      </dl>
      {topQuestion && (
        <div className={`${ROW} border-t`}>
          <span className="rounded-md bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800 dark:bg-orange-950/50 dark:text-orange-300">
            Most asked, not resolved
          </span>
          <p className="min-w-0 flex-1 basis-60 text-sm [text-wrap:pretty]">{topQuestion}</p>
          <Button variant="link" size="sm" className="h-auto px-0" onClick={onQuestions}>
            <span>See the questions</span>
            <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </div>
      )}
      {latest && (
        <div className={`${ROW} border-t`}>
          <p className="min-w-0 flex-1 basis-60 text-sm [text-wrap:pretty]">
            {latestChangeLine(latest, learners.scope === "agents")}
          </p>
          <ChangeStateBadge state={describeChange(latest, timezone).state} />
          <Button variant="link" size="sm" className="h-auto px-0" onClick={onAllChanges}>
            <span>All changes</span>
            <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </div>
      )}
      <div className="border-t bg-muted/30 px-4 py-3 sm:px-6">
        <LearnersSetting learners={learners} />
      </div>
    </Card>
  )
}

function Headline({
  learners,
  waiting,
  listed,
  tally,
  onReview,
}: {
  learners: Learners;
  waiting: number;
  listed: Suggestion[];
  tally: string;
  onReview: () => void;
}) {
  const heading = waiting > 0 ? readyHeading(waiting, learners.scope) : learningHeading(learners.scope, isLearning(learners))
  const line = waiting > 0 ? draftedFromLine(listed) : tally

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 bg-violet-50/70 px-4 py-4 dark:bg-violet-950/30 sm:px-6">
      <div className="flex min-w-0 flex-1 basis-80 items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-violet-700 text-white dark:bg-violet-600">
          <Sparkles className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 space-y-1">
          <h2 className="text-lg font-semibold leading-snug [text-wrap:balance]">{heading}</h2>
          <p className="text-sm text-muted-foreground [text-wrap:pretty]">{line}</p>
        </div>
      </div>
      {waiting > 0 && (
        <Button className="bg-violet-700 text-white hover:bg-violet-800 dark:bg-violet-600" onClick={onReview}>
          <span>{waiting === 1 ? "Review 1 improvement" : `Review ${waiting.toLocaleString()} improvements`}</span>
          <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
        </Button>
      )}
    </div>
  )
}

function Figure({ label, value, note, accent = false }: { label: string; value: string; note: string; accent?: boolean }) {
  return (
    <div className="min-w-0 border-b px-4 py-3 last:border-b-0 sm:px-6 lg:border-b-0 lg:border-r lg:last:border-r-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className={accent ? "text-2xl font-bold tabular-nums text-violet-700 dark:text-violet-300" : "text-2xl font-bold tabular-nums"}>
        {value}
      </dd>
      <dd className="text-xs text-muted-foreground">{note}</dd>
    </div>
  )
}

/** One agent: the room its instructions leave; several: how many of them learn. */
function LearnersFigure({ learners }: { learners: Learners }) {
  switch (learners.scope) {
    case "agent":
      return (
        <Figure
          label="Room in instructions"
          value={roomLines(learners.agent.instructions).toLocaleString()}
          note={`more lines fit · ${instructionsRoomLabel(learners.agent.instructions)}`}
        />
      )
    case "agents":
      return (
        <Figure
          label="Agents learning"
          value={learners.learning.toLocaleString()}
          note={`of ${learners.total.toLocaleString()} agents`}
        />
      )
    default: {
      const exhaustive: never = learners
      return exhaustive
    }
  }
}

function readyHeading(waiting: number, learner: Learners["scope"]): string {
  const improvements = waiting === 1 ? "1 improvement is" : `${waiting.toLocaleString()} improvements are`
  return `${improvements} ready for your ${learner === "agent" ? "agent" : "agents"}`
}

/**
 * «Drafted from conversations not resolved: Order status (27), Contact request (16).» Each question keeps its own count:
 * one session may back drafts on two questions, so a sum would overstate.
 */
function draftedFromLine(listed: Suggestion[]): string {
  const sessions = new Map<string, number>()
  for (const { topicLabel, sessions: count } of listed) {
    sessions.set(topicLabel, Math.max(sessions.get(topicLabel) ?? 0, count))
  }
  const questions = [...sessions]
    .slice(0, 3)
    .map(([label, count]) => (count > 0 ? `${label} (${count.toLocaleString()})` : label))
  return questions.length > 0
    ? `Drafted from conversations not resolved: ${questions.join(", ")}.`
    : "Drafted from conversations not resolved."
}

/** The earliest closing date only while the queue lists every waiting draft: an unlisted one is older. */
function waitingNote(suggestions: QualitySuggestionsResult | undefined, timezone: string): string {
  const listed = suggestions?.suggestions ?? []
  const waiting = suggestions?.waiting ?? 0
  const oldest = listed.at(-1)
  if (waiting === 0) return "nothing waits for you"
  if (!oldest || listed.length < waiting) return "each closes about four weeks after it was drafted"
  return `the first closes ${closingDate(oldest.createdAt, timezone)}`
}

function questionCount(topics: UnresolvedTopicsResult): number {
  return topics.categories.reduce((sum, category) => sum + category.topics.length, 0)
}

/** «Holster compatibility (Fit and choice) · 43 conversations · 15 gave up». */
function mostAsked(topics: UnresolvedTopicsResult | undefined): string | null {
  const [top] = (topics?.categories ?? [])
    .flatMap((category) => category.topics.map((topic) => ({ ...topic, category: category.category })))
    .sort((a, b) => b.sessions - a.sessions)
  if (!top) return null

  return [
    questionWithKind(top.label, top.category),
    top.sessions === 1 ? "1 conversation" : `${top.sessions.toLocaleString()} conversations`,
    `${top.gaveUp.toLocaleString()} gave up`,
  ].join(" · ")
}

function isLearning(learners: Learners): boolean {
  switch (learners.scope) {
    case "agent":
      return learners.agent.mode !== "off"
    case "agents":
      return learners.learning > 0
    default: {
      const exhaustive: never = learners
      return exhaustive
    }
  }
}
